import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import Card from '../ui/Card';

/**
 * AgentFormStep4 - Memory Settings
 */
const AgentFormStep4 = ({ formData, onChange, errors }) => {
  const memoryTypes = [
    {
      value: 'ephemeral',
      label: 'Ephemeral',
      description: 'No memory between conversations. Each interaction starts fresh.',
      icon: '💨',
      useCase: 'One-off queries, stateless interactions',
    },
    {
      value: 'short_term',
      label: 'Short-term',
      description: 'Maintains context within a single conversation session.',
      icon: '🔄',
      useCase: 'Multi-turn conversations, task completion',
    },
    {
      value: 'long_term',
      label: 'Long-term',
      description: 'Persists memory across multiple sessions and conversations.',
      icon: '🧠',
      useCase: 'Personal assistants, learning agents',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
          Memory Settings
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure how your agent remembers information from previous interactions.
        </p>
      </div>

      {/* Memory Type Selection */}
      <div>
        <label className="block text-sm font-semibold mb-3 text-gray-900 dark:text-spooky-ghost-white">
          Memory Type *
        </label>
        <div className="space-y-3">
          {memoryTypes.map((type) => (
            <Card
              key={type.value}
              padding="comfortable"
              className={`cursor-pointer transition-all border-2 ${
                formData.memory_type === type.value
                  ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
                  : 'border-transparent hover:border-pink-300 dark:hover:border-pink-700'
              }`}
              onClick={() => onChange({ target: { name: 'memory_type', value: type.value } })}
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl flex-shrink-0">{type.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                      {type.label}
                    </h4>
                    {formData.memory_type === type.value && (
                      <span className="px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                    {type.description}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <strong>Best for:</strong> {type.useCase}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
        {errors.memory_type && (
          <p className="text-sm text-red-500 mt-2">{errors.memory_type}</p>
        )}
      </div>

      {/* Max Context Window */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-900 dark:text-spooky-ghost-white" htmlFor="max_context_window">
          Max Context Window: {formData.max_context_window.toLocaleString()} tokens
        </label>
        <input
          type="range"
          id="max_context_window"
          name="max_context_window"
          min="1024"
          max="32768"
          step="1024"
          value={formData.max_context_window}
          onChange={onChange}
          className="w-full h-2 bg-gray-200 dark:bg-spooky-bg-light rounded-lg appearance-none cursor-pointer accent-pink-500"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>1K</span>
          <span>8K</span>
          <span>16K</span>
          <span>32K</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          The maximum number of tokens the agent can remember at once. Higher values allow longer conversations but use more resources.
        </p>
      </div>

      {/* Info Card */}
      <Card padding="comfortable" className="bg-blue-50 dark:bg-blue-900/10 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
              Understanding Context Windows
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              The context window determines how much conversation history the agent can reference:
            </p>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 mt-2">
              <li>• <strong>1K-4K tokens:</strong> Short conversations, quick responses</li>
              <li>• <strong>8K-16K tokens:</strong> Medium-length discussions, code analysis</li>
              <li>• <strong>32K tokens:</strong> Long conversations, document review</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AgentFormStep4;
